import { motion, AnimatePresence } from 'motion/react';
import { X, Instagram, Youtube, Users, ThumbsUp, MessageSquare, Eye, Film } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';

// Social Media Icons
const TikTokIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" fill="currentColor"/>
  </svg>
);

const SnapchatIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12.206 2.024c-.03 0-.062 0-.091.003-1.023.039-1.99.395-2.712 1.002-.814.686-1.295 1.648-1.382 2.622-.037.416-.014 1.238.008 1.758.01.246.018.436.018.567 0 .355-.027.614-.09.861-.103.408-.295.72-.605.979-.248.208-.431.331-.669.449l-.017.008c-.21.105-.463.231-.618.42-.186.226-.238.525-.142.818.13.399.493.661.905.655.058 0 .122-.006.192-.018.232-.04.433-.062.615-.062.172 0 .315.019.471.063.026.007.058.013.095.021.011.002.021.005.032.007.047.011.095.021.144.03l.005.001c.227.043.464.088.686.179.273.111.487.274.653.498.134.182.239.399.32.663.082.266.132.55.161.867.022.239.049.51.108.772.062.28.181.576.407.786.16.149.367.229.599.229.094 0 .192-.012.292-.037.36-.088.737-.133 1.12-.133.415 0 .803.051 1.152.151.098.028.19.043.277.043.22 0 .421-.075.578-.214.224-.197.346-.486.412-.773.063-.272.091-.551.114-.797.029-.312.078-.593.159-.858.08-.262.186-.477.323-.659.167-.222.382-.385.657-.496.224-.091.463-.136.691-.179l.005-.001c.049-.009.097-.019.144-.03.011-.002.022-.005.033-.007.036-.008.068-.014.094-.021.155-.044.298-.063.469-.063.181 0 .382.022.612.062.07.012.134.018.192.018.411.006.774-.256.904-.654.097-.294.044-.593-.141-.819-.155-.189-.408-.315-.619-.42l-.016-.008c-.239-.118-.422-.241-.67-.449-.31-.259-.502-.571-.605-.979-.063-.247-.09-.506-.09-.861 0-.131.008-.321.018-.567.022-.52.045-1.342.008-1.758-.087-.974-.568-1.936-1.382-2.622-.722-.607-1.689-.963-2.712-1.002-.029-.003-.061-.003-.091-.003"/>
  </svg>
);

export interface CreatorProfile {
  id: string;
  name: string;
  handle: string;
  avatar: string;
  coverImage?: string;
  bio?: string;
  location?: string;
  website?: string;
  email?: string;
  
  // Social Media Stats
  instagram?: {
    followers: string;
    handle: string;
  };
  youtube?: {
    subscribers: string;
    handle: string;
  };
  tiktok?: {
    followers: string;
    handle: string;
  };
  snapchat?: {
    followers: string;
    handle: string;
  };
  
  // Performance Metrics
  avgFollowers: string;
  engagementRate: string;
  avgComments: string;
  totalCampaigns: number;
  collabBrandCount: number;
  campaignNetProfit: string;
  
  // Content Portfolio
  contentPortfolio: {
    id: string;
    thumbnail: string;
    views: string;
    likes: string;
    date: string;
  }[];
  
  // Audience Analytics
  audienceAge: {
    range: string;
    percentage: number;
  }[];
  audienceGender: {
    male: number;
    female: number;
  };
  audienceLocation: {
    country: string;
    percentage: number;
  }[];
  
  // Interests & Tags
  interests: string[];
  tags: string[];
}

interface CreatorProfileSidebarProps {
  creator: CreatorProfile | null;
  isOpen: boolean;
  onClose: () => void;
}

export function CreatorProfileSidebar({ creator, isOpen, onClose }: CreatorProfileSidebarProps) {
  if (!creator) return null;

  // Calculate average likes per post (using engagement rate as proxy)
  const avgLikes = creator.engagementRate ? 
    Math.round(parseFloat(creator.avgFollowers.replace(/[^0-9.]/g, '')) * parseFloat(creator.engagementRate) / 100 * 1000) : 0;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/50 z-[100]"
            onClick={onClose}
          />

          {/* Sidebar */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
            className="fixed right-0 top-0 bottom-0 w-[60%] bg-white z-[101] overflow-y-auto"
          >
            {/* Close Button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="absolute top-4 right-4 z-10 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full"
            >
              <X className="w-5 h-5" />
            </Button>

            {/* Header Section */}
            <div className="px-12 py-8">
              <div className="flex items-center gap-8 mb-8">
                {/* Large Circular Avatar */}
                <img
                  src={creator.avatar}
                  alt={creator.name}
                  className="w-48 h-48 rounded-full object-cover flex-shrink-0"
                />
                
                {/* Name */}
                <div className="flex-1">
                  <h1 className="text-5xl text-gray-900 mb-2 uppercase tracking-tight">
                    {creator.name}
                  </h1>
                </div>
              </div>

              {/* Categories Section */}
              {(creator.interests.length > 0 || creator.tags.length > 0) && (
                <Card className="bg-[#f8f9fa] border-0 mb-8">
                  <CardContent className="p-6">
                    <h2 className="text-gray-900 mb-4">Categories</h2>
                    <div className="flex flex-wrap gap-2">
                      {creator.interests.map((interest, idx) => (
                        <Badge 
                          key={idx} 
                          className="bg-white text-gray-700 border border-gray-200 px-4 py-2 text-sm hover:bg-gray-50"
                        >
                          {interest}
                        </Badge>
                      ))}
                      {creator.tags.map((tag, idx) => (
                        <Badge 
                          key={`tag-${idx}`} 
                          className="bg-white text-gray-700 border border-gray-200 px-4 py-2 text-sm hover:bg-gray-50"
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Who is Section */}
            <div className="bg-[#f8f9fa] px-12 py-8">
              <h2 className="text-gray-900 mb-4">Who is {creator.name}</h2>
              <p className="text-gray-700 mb-2">{creator.bio}</p>
              {creator.email && (
                <p className="text-gray-700">{creator.email}</p>
              )}
            </div>

            {/* Linked Platforms */}
            <div className="bg-white px-12 py-8">
              <h2 className="text-gray-900 mb-4">Linked platforms</h2>
              <div className="space-y-3">
                {creator.instagram && (
                  <a 
                    href={`https://instagram.com/${creator.instagram.handle}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-blue-600 hover:underline"
                  >
                    <Instagram className="w-5 h-5" />
                    <span className="text-lg">{creator.instagram.handle}</span>
                  </a>
                )}
                {creator.youtube && (
                  <a 
                    href={`https://youtube.com/@${creator.youtube.handle}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-blue-600 hover:underline"
                  >
                    <Youtube className="w-5 h-5" />
                    <span className="text-lg">{creator.youtube.handle}</span>
                  </a>
                )}
                {creator.tiktok && (
                  <a 
                    href={`https://tiktok.com/@${creator.tiktok.handle}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-blue-600 hover:underline"
                  >
                    <TikTokIcon />
                    <span className="text-lg">{creator.tiktok.handle}</span>
                  </a>
                )}
                {creator.snapchat && (
                  <a 
                    href={`https://snapchat.com/add/${creator.snapchat.handle}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-blue-600 hover:underline"
                  >
                    <SnapchatIcon />
                    <span className="text-lg">{creator.snapchat.handle}</span>
                  </a>
                )}
              </div>
            </div>

            {/* Metrics Section */}
            <div className="bg-white px-12 py-8">
              <h2 className="text-gray-900 mb-6">Metrics</h2>
              
              {/* Metric Grid */}
              <div className="grid grid-cols-3 gap-6 mb-8">
                {/* Followers */}
                <div className="text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Users className="w-7 h-7 text-gray-600" />
                  </div>
                  <p className="text-sm text-gray-500 mb-1">Followers</p>
                  <p className="text-3xl text-gray-900">{creator.avgFollowers}</p>
                </div>

                {/* Engagement */}
                <div className="text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <svg className="w-7 h-7 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <p className="text-sm text-gray-500 mb-1">Engagement</p>
                  <p className="text-3xl text-gray-900">{creator.engagementRate}</p>
                </div>

                {/* Avg likes/post */}
                <div className="text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <ThumbsUp className="w-7 h-7 text-gray-600" />
                  </div>
                  <p className="text-sm text-gray-500 mb-1">Avg. likes/post</p>
                  <p className="text-3xl text-gray-900">{avgLikes > 0 ? `${(avgLikes / 1000).toFixed(1)}K` : '2.5K'}</p>
                </div>

                {/* Avg comments/post */}
                <div className="text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <MessageSquare className="w-7 h-7 text-gray-600" />
                  </div>
                  <p className="text-sm text-gray-500 mb-1">Avg. comments/post</p>
                  <p className="text-3xl text-gray-900">{creator.avgComments}</p>
                </div>

                {/* Avg views/post */}
                <div className="text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Eye className="w-7 h-7 text-gray-600" />
                  </div>
                  <p className="text-sm text-gray-500 mb-1">Avg. views/post</p>
                  <p className="text-3xl text-gray-900">-</p>
                </div>

                {/* Avg reels-views/post */}
                <div className="text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Film className="w-7 h-7 text-gray-600" />
                  </div>
                  <p className="text-sm text-gray-500 mb-1">Avg. reels-views/post</p>
                  <p className="text-3xl text-gray-900">171.2K</p>
                </div>
              </div>

              {/* Audience Insights */}
              <div className="mt-12">
                <h2 className="text-gray-900 mb-6">Audience insights</h2>

                {/* Gender Split */}
                <div className="mb-8">
                  <h3 className="text-gray-900 mb-6">Gender split</h3>
                  
                  {/* Donut Chart */}
                  <div className="flex justify-center mb-6">
                    <svg width="200" height="200" viewBox="0 0 200 200">
                      <circle
                        cx="100"
                        cy="100"
                        r="70"
                        fill="none"
                        stroke="#f472b6"
                        strokeWidth="40"
                        strokeDasharray={`${creator.audienceGender.female * 4.4} 440`}
                        transform="rotate(-90 100 100)"
                      />
                      <circle
                        cx="100"
                        cy="100"
                        r="70"
                        fill="none"
                        stroke="#fbbf24"
                        strokeWidth="40"
                        strokeDasharray={`${creator.audienceGender.male * 4.4} 440`}
                        strokeDashoffset={`-${creator.audienceGender.female * 4.4}`}
                        transform="rotate(-90 100 100)"
                      />
                    </svg>
                  </div>

                  {/* Legend */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-yellow-400 rounded-sm"></div>
                        <span className="text-gray-700">Male</span>
                      </div>
                      <span className="text-gray-900">{creator.audienceGender.male}%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-pink-400 rounded-sm"></div>
                        <span className="text-gray-700">Female</span>
                      </div>
                      <span className="text-gray-900">{creator.audienceGender.female}%</span>
                    </div>
                  </div>
                </div>

                {/* Age Division */}
                <div className="mb-8">
                  <h3 className="text-gray-900 mb-6">Age division</h3>
                  
                  {/* Bar Chart */}
                  <div className="flex items-end justify-around h-48 mb-6">
                    {creator.audienceAge.map((age, idx) => (
                      <div key={idx} className="flex flex-col items-center gap-2">
                        <div 
                          className="w-12 bg-pink-400 rounded-t"
                          style={{ height: `${age.percentage * 2}px` }}
                        ></div>
                        <span className="text-xs text-gray-600">{age.range}</span>
                      </div>
                    ))}
                  </div>

                  {/* Gender Age Info */}
                  <div className="space-y-3 pt-6 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-yellow-400 rounded-sm"></div>
                        <div>
                          <span className="text-gray-700">Male</span>
                          <p className="text-sm text-gray-500">Avg. age: {Math.round(creator.audienceAge.reduce((acc, curr) => acc + parseInt(curr.range.split('-')[0]) * curr.percentage, 0) / 100)}y</p>
                        </div>
                      </div>
                      <span className="text-gray-900">{creator.audienceGender.male}%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-pink-400 rounded-sm"></div>
                        <div>
                          <span className="text-gray-700">Female</span>
                          <p className="text-sm text-gray-500">Avg. age: {Math.round(creator.audienceAge.reduce((acc, curr) => acc + parseInt(curr.range.split('-')[0]) * curr.percentage, 0) / 100)}y</p>
                        </div>
                      </div>
                      <span className="text-gray-900">{creator.audienceGender.female}%</span>
                    </div>
                  </div>
                </div>

                {/* Top Countries */}
                <div>
                  <h3 className="text-gray-900 mb-4">Top countries</h3>
                  <div className="space-y-3">
                    {creator.audienceLocation.map((location, idx) => (
                      <div key={idx} className="flex items-center justify-between py-2 border-b border-gray-100">
                        <span className="text-gray-700">{location.country}</span>
                        <span className="text-gray-900">{location.percentage}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
